import { createFileRoute, redirect } from "@tanstack/react-router";
import Landing from "@/pages/Landing";
import { roomPath } from "@/engine/state/roomIdentity";

/**
 * The root, which is the landing everywhere and a signpost for old links.
 *
 * The landing is what somebody reads before deciding (#742), so `/` draws it on
 * every host — the logo on `/home` leads here, and on the app's name it must
 * not bounce back. Two addresses still answer for themselves in `beforeLoad`:
 *
 * - the invitation this application used to hand out, `?room=<code>` (and the
 *   `?enter=join&room=` beside it), which names a room and has an address;
 * - a sign-in round trip, which arrives carrying what the identity provider
 *   gave it and is sent on to `/home` with the query intact — the client reads
 *   it out of the address there, and dropping it would sign them out at the
 *   door.
 *
 * Anything else on the query — an ad's click id, a share's tag — is the
 * landing's own address and renders, or the people a campaign paid for would
 * never see the page meant for them.
 */
export const Route = createFileRoute("/")({
  // Everything, rather than the two this route reads: what it does not
  // understand belongs to whoever does, and is carried rather than dropped.
  validateSearch: (search: Record<string, unknown>) => search,
  beforeLoad: ({ search }) => {
    const room = typeof search.room === "string" ? search.room : undefined;
    if (room) throw redirect({ to: roomPath(room), search });
    // `enter` is this application's half of the round trip: `landingUrlFor`
    // points the provider at `/`, and a popup sign-in arrives with nothing else.
    const roundTrip = [
      "enter",
      "code",
      "error",
      "error_code",
      "error_description",
    ].some((key) => search[key] !== undefined);
    if (roundTrip || search.room !== undefined) {
      throw redirect({ to: "/home", search: { ...search, room: undefined } });
    }
  },
  component: Landing,
});
